import { create } from 'zustand';
import { CustomerProfile, CustomerProfileFormData } from '../types/customer';
import { supabase } from '../lib/supabase';
import { parseTierCapError } from '../lib/tierCapError';

interface CustomerStore {
  customers: CustomerProfile[];
  selectedCustomer: CustomerProfile | null;
  addCustomer: (data: CustomerProfileFormData) => Promise<CustomerProfile | null>;
  updateCustomer: (id: string, data: Partial<CustomerProfileFormData>) => Promise<void>;
  selectCustomer: (customerOrId: string | CustomerProfile) => void;
  clearSelectedCustomer: () => void;
  fetchCustomers: () => Promise<void>;
}

export const useCustomerStore = create<CustomerStore>((set, get) => ({
  customers: [],
  selectedCustomer: null,

  addCustomer: async (data) => {
    try {
      // Normalize email to lowercase
      const normalizedEmail = data.email.toLowerCase().trim();

      const { data: newCustomer, error } = await supabase
        .from('customers')
        .insert({
          professional_id: data.professionalId,
          first_name: data.firstName,
          last_name: data.lastName,
          email: normalizedEmail,
          email_lower: normalizedEmail,
          phone: data.phone,
          handicap: data.handicap,
          swing_speed: data.swingSpeed,
          height: data.height,
          wrist_to_floor: data.wristToFloor,
          playing_frequency: data.playingFrequency,
          preferred_ball_flight: data.preferredBallFlight,
          handed: data.handed,
          notes: data.notes,
          street: data.street,
          suburb: data.suburb,
          state: data.state,
          postcode: data.postcode,
          country: data.country
        })
        .select()
        .single();

      if (error) {
        // D12b — Pro tier cap (e.g. customer_db on the basic tier) is
        // enforced by a Postgres trigger. Re-throw a structured error so
        // the form can render a tier-aware upgrade prompt; other errors
        // fall through to the existing duplicate-email handling +
        // silent-null pattern for backwards compatibility.
        const tierCap = parseTierCapError(error);
        if (tierCap) {
          const e: Error & { tierCap?: typeof tierCap } = new Error(tierCap.message);
          e.tierCap = tierCap;
          throw e;
        }
        // Check if this is a duplicate email error
        if (error.code === '23505' && error.message.includes('customers_email_lower')) {
          // Try to fetch the existing customer
          const { data: existingCustomer, error: fetchError } = await supabase
            .from('customers')
            .select('*')
            .eq('email_lower', normalizedEmail)
            .single();

          if (fetchError) throw fetchError;

          if (existingCustomer) {
            // Format the existing customer profile
            const customerProfile: CustomerProfile = {
              id: existingCustomer.id,
              professionalId: existingCustomer.professional_id,
              firstName: existingCustomer.first_name,
              lastName: existingCustomer.last_name,
              email: existingCustomer.email,
              phone: existingCustomer.phone,
              handicap: existingCustomer.handicap,
              swingSpeed: existingCustomer.swing_speed,
              height: existingCustomer.height,
              wristToFloor: existingCustomer.wrist_to_floor,
              playingFrequency: existingCustomer.playing_frequency,
              preferredBallFlight: existingCustomer.preferred_ball_flight,
              handed: existingCustomer.handed,
              notes: existingCustomer.notes,
              street: existingCustomer.street,
              suburb: existingCustomer.suburb,
              state: existingCustomer.state,
              postcode: existingCustomer.postcode,
              country: existingCustomer.country,
              createdAt: existingCustomer.created_at,
              updatedAt: existingCustomer.updated_at
            };

            // Update the customers list with the existing customer
            await get().fetchCustomers();

            // Set the selected customer
            set(state => ({
              selectedCustomer: customerProfile
            }));

            return customerProfile;
          }
        }

        throw error;
      }

      const formattedCustomer: CustomerProfile = {
        id: newCustomer.id,
        professionalId: newCustomer.professional_id,
        firstName: newCustomer.first_name,
        lastName: newCustomer.last_name,
        email: newCustomer.email,
        phone: newCustomer.phone,
        handicap: newCustomer.handicap,
        swingSpeed: newCustomer.swing_speed,
        height: newCustomer.height,
        wristToFloor: newCustomer.wrist_to_floor,
        playingFrequency: newCustomer.playing_frequency,
        preferredBallFlight: newCustomer.preferred_ball_flight,
        handed: newCustomer.handed,
        notes: newCustomer.notes,
        street: newCustomer.street,
        suburb: newCustomer.suburb,
        state: newCustomer.state,
        postcode: newCustomer.postcode,
        country: newCustomer.country,
        createdAt: newCustomer.created_at,
        updatedAt: newCustomer.updated_at
      };

      // Update the customers list with the new customer
      await get().fetchCustomers();

      // Set the selected customer
      set(state => ({
        selectedCustomer: formattedCustomer
      }));

      return formattedCustomer;
    } catch (err) {
      // D12b — propagate tier cap errors so the form can show the
      // upgrade prompt. Any other failure stays silent (returns null)
      // to preserve the existing caller contract.
      if (err && typeof err === 'object' && 'tierCap' in err) {
        throw err;
      }
      return null;
    }
  },

  updateCustomer: async (id, data) => {
    try {
      // Normalize email to lowercase if provided
      const updateData = {
        ...data,
        email: data.email ? data.email.toLowerCase().trim() : undefined
      };

      const { error } = await supabase
        .from('customers')
        .update({
          professional_id: updateData.professionalId,
          first_name: updateData.firstName,
          last_name: updateData.lastName,
          email: updateData.email,
          phone: updateData.phone,
          handicap: updateData.handicap,
          swing_speed: updateData.swingSpeed,
          height: updateData.height,
          wrist_to_floor: updateData.wristToFloor,
          playing_frequency: updateData.playingFrequency,
          preferred_ball_flight: updateData.preferredBallFlight,
          handed: updateData.handed,
          notes: updateData.notes,
          street: updateData.street,
          suburb: updateData.suburb,
          state: updateData.state,
          postcode: updateData.postcode,
          country: updateData.country
        })
        .eq('id', id);

      if (error) throw error;

      // Refresh the customers list to get the updated data
      await get().fetchCustomers();

      // Update the selected customer if it's the one being edited
      const { customers, selectedCustomer } = get();
      if (selectedCustomer?.id === id) {
        const updatedCustomer = customers.find(c => c.id === id);
        if (updatedCustomer) {
          set({ selectedCustomer: updatedCustomer });
        }
      }
    } catch (err) {
      throw err;
    }
  },

  selectCustomer: (customerOrId) => {
    set(state => {
      let customer: CustomerProfile | null = null;

      if (typeof customerOrId === 'string') {
        // Legacy behavior: find customer by ID
        customer = state.customers.find(c => c.id === customerOrId) || null;
      } else {
        // New behavior: use the full customer object
        customer = customerOrId;
      }

      // Store the selected customer ID in localStorage for checkout
      if (customer) {
        localStorage.setItem('selectedCustomerId', customer.id);
      } else {
        localStorage.removeItem('selectedCustomerId');
      }
      return { selectedCustomer: customer };
    });
  },

  clearSelectedCustomer: () => {
    set({ selectedCustomer: null });
  },

  fetchCustomers: async () => {
    try {
      const { data: session } = await supabase.auth.getSession();

      if (!session?.session?.user) {
        throw new Error('Not authenticated');
      }

      // Use professional_customers join - RLS restricts to assigned customers
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedCustomers: CustomerProfile[] = (data || []).map(customer => ({
        id: customer.id,
        professionalId: customer.professional_id,
        firstName: customer.first_name,
        lastName: customer.last_name,
        email: customer.email,
        phone: customer.phone,
        handicap: customer.handicap,
        swingSpeed: customer.swing_speed,
        height: customer.height,
        wristToFloor: customer.wrist_to_floor,
        playingFrequency: customer.playing_frequency,
        preferredBallFlight: customer.preferred_ball_flight,
        handed: customer.handed,
        notes: customer.notes,
        street: customer.street,
        suburb: customer.suburb,
        state: customer.state,
        postcode: customer.postcode,
        country: customer.country,
        createdAt: customer.created_at,
        updatedAt: customer.updated_at
      }));

      set({ customers: formattedCustomers });

      // If there's a selected customer, update it with the latest data
      const { selectedCustomer } = get();
      if (selectedCustomer) {
        const updatedCustomer = formattedCustomers.find(c => c.id === selectedCustomer.id);
        if (updatedCustomer) {
          set({ selectedCustomer: updatedCustomer });
        }
      }
    } catch (err) {
      throw err;
    }
  }
}));
